import { Container, Graphics } from "pixi.js";

export type PedalFlashProps = {
  width: number;
  playheadY: number;
  /** De 1 à l'enfoncement à 0 une fraction de seconde plus tard. */
  strength: number;
};

/**
 * L'éclat de l'enfoncement : tous les étouffoirs se lèvent d'un coup.
 *
 * C'est le seul instant de la pédale qui soit un geste, et le geste appelle
 * une réponse — sans elle, appuyer sur la pédale ne produit rien à l'écran
 * tant qu'aucune note ne traîne encore. Une lueur brève le long de la tête de
 * lecture, sur toute la largeur : ce qui vient de changer concerne le clavier
 * entier, pas une touche.
 *
 * Réservé à **ton** pied. La pédale du morceau bouge à chaque changement
 * d'harmonie, et un éclat à chacun ferait clignoter la page d'un bout à
 * l'autre du morceau.
 *
 * Bref, et sans rien masquer : quelques bandes de plus en plus pâles de part
 * et d'autre de la ligne, jamais un aplat. Le roll porte déjà le voile du
 * passé et la vignette ; une troisième couche opaque, même une demi-seconde,
 * cacherait les notes qui passent la ligne au moment précis où on les joue.
 */
export class PedalFlash {
  private readonly graphics = new Graphics();

  constructor(stage: Container) {
    stage.addChild(this.graphics);
  }

  destroy(): void {
    this.graphics.destroy();
  }

  draw(props: PedalFlashProps): void {
    const { width, playheadY, strength } = props;

    this.graphics.clear();

    if (strength <= 0) {
      return;
    }

    for (let i = 0; i < BANDS; i++) {
      const spread = (i + 1) / BANDS;
      const alpha = PEAK_ALPHA * strength * (1 - spread) ** 2;

      if (alpha <= 0.002) {
        continue;
      }

      const reach = REACH_PX * spread;

      this.graphics
        .rect(0, playheadY - reach, width, reach * 2)
        .fill({ color: FLASH_COLOR, alpha });
    }
  }
}

const FLASH_COLOR = 0xe8eef7;
/** Hauteur atteinte par la lueur de part et d'autre de la ligne. */
const REACH_PX = 26;
const BANDS = 5;
const PEAK_ALPHA = 0.22;
